import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Loader2, Check } from 'lucide-react';
import { InlineEditField } from '@/components/shared/InlineEditField';
import { InlineLocationSelect } from '@/components/shared/InlineLocationSelect';
import { Button } from '@/components/ui/button';
import { cn, HOVER_REVEAL } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Panel } from '@/components/ui/panel';
import { useManagers } from '@/hooks/useManagers';
import type { Manager } from '@jobos/shared/types/manager';
import { formatManagerName } from '@jobos/shared';
import { ChangeClientModal } from './ChangeClientModal';

interface JobInfoCardProps {
  jobId: string;
  jobCode?: string | null;
  clientId: string;
  clientName: string;
  manager?: Manager | null;
  locationId: string;
  locationAddressLine1: string | null | undefined;
  locationAddressLine2: string | null | undefined;
  locationCity: string | null | undefined;
  serviceAddressNote?: string | null;
  onUpdateField: (field: string, value: any) => Promise<void>;
}

export function JobInfoCard({
  jobId,
  jobCode,
  clientId,
  clientName,
  manager,
  locationId,
  locationAddressLine1,
  locationAddressLine2,
  locationCity,
  serviceAddressNote,
  onUpdateField,
}: JobInfoCardProps) {
  const [changeClientOpen, setChangeClientOpen] = useState(false);
  const [managerPopoverOpen, setManagerPopoverOpen] = useState(false);
  const [isSavingManager, setIsSavingManager] = useState(false);
  const { data: managers = [] } = useManagers(clientId);

  const handleManagerSelect = async (managerId: string | null) => {
    setManagerPopoverOpen(false);
    if (managerId === (manager?.id || null)) return;
    setIsSavingManager(true);
    try {
      await onUpdateField('managerId', managerId);
    } finally {
      setIsSavingManager(false);
    }
  };

  return (
    <>
    <Panel title="Job Info" className="flex-1">
        {jobCode && (
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
              Job Code
            </label>
            <div className="text-lg font-mono font-semibold text-blue-600">
              {jobCode}
            </div>
          </div>
        )}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-bold text-gray-700 uppercase">
              Client
            </label>
            <button
              type="button"
              // Negative margin keeps the label where it was while the tappable
              // box grows to a finger-sized target.
              className="-my-2 -mr-2 px-2 py-2 text-xs font-medium text-blue-600 hover:text-blue-800"
              onClick={() => setChangeClientOpen(true)}
            >
              Change
            </button>
          </div>
          <Link
            to={`/clients/${clientId}`}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            {clientName}
          </Link>
        </div>
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
            Manager
          </label>
          <Popover open={managerPopoverOpen} onOpenChange={setManagerPopoverOpen}>
            <PopoverAnchor asChild>
              <div className="group flex items-start justify-between">
                <div className="text-sm text-gray-900 flex items-start gap-2">
                  {manager ? (
                    <div>
                      <div className="font-medium">
                        {formatManagerName(manager.name, manager.firstName, manager.lastName)}
                      </div>
                      {manager.email && (
                        <div className="text-gray-600">Email: {manager.email}</div>
                      )}
                      {manager.phone && (
                        <div className="text-gray-600">Phone: {manager.phone}</div>
                      )}
                    </div>
                  ) : (
                    <div className="text-gray-500">No manager</div>
                  )}
                  {isSavingManager && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
                {!isSavingManager && (
                  <PopoverTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Change manager"
                      className={cn('h-9 shrink-0 px-3 sm:h-7 sm:px-2', HOVER_REVEAL)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </PopoverTrigger>
                )}
              </div>
            </PopoverAnchor>
            <PopoverContent className="p-0 w-64" align="start">
              <Command
                filter={(value: string, search: string) => {
                  if (value.toLowerCase().includes(search.toLowerCase())) return 1;
                  return 0;
                }}
              >
                <CommandInput placeholder="Search managers..." />
                <CommandList>
                  <CommandEmpty>No managers found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="No Manager"
                      onSelect={() => handleManagerSelect(null)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4 shrink-0',
                          !manager ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="text-gray-500">No Manager</span>
                    </CommandItem>
                    {managers.map((m) => (
                      <CommandItem
                        key={m.id}
                        value={formatManagerName(m.name, m.firstName, m.lastName)}
                        onSelect={() => handleManagerSelect(m.id)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4 shrink-0',
                            manager?.id === m.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        {formatManagerName(m.name, m.firstName, m.lastName)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
            Service Address
          </label>
          <InlineLocationSelect
            clientId={clientId}
            locationId={locationId}
            displayContent={
              <>
                <div>{locationAddressLine1}</div>
                {locationAddressLine2 && <div>{locationAddressLine2}</div>}
                <div>{locationCity}</div>
              </>
            }
            onSave={(newLocationId) => onUpdateField('locationId', newLocationId)}
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">
            Address Note
          </label>
          <InlineEditField
            value={serviceAddressNote || ''}
            onSave={(value) => onUpdateField('serviceAddressNote', value || null)}
            placeholder="Add address note..."
            editTrigger="click"
          />
        </div>
    </Panel>

      <ChangeClientModal
        open={changeClientOpen}
        onOpenChange={setChangeClientOpen}
        jobId={jobId}
        currentClientId={clientId}
      />
    </>
  );
}
